import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { 
  Plus, 
  X, 
  ShoppingCart, 
  Search,
  Loader2,
  Sparkles,
  Heart,
  Clock,
  Utensils,
  ChevronRight,
  Check,
  Undo2,
  AlertTriangle,
  PartyPopper
} from 'lucide-react';
import { format, startOfWeek, addDays, isToday, isTomorrow, isPast, isAfter } from 'date-fns';
import { haptic } from '@/utils/haptics';

/**
 * Mobile-optimized Meal Planner Component
 * Gold Standard UX: Plan a week in under 60 seconds using one thumb
 */
const MobileMealPlanner = ({ 
  mealPlan, 
  onAddMeal, 
  onRemoveMeal, 
  onAutoPlan, 
  onGenerateGroceryList,
  isPro,
  favorites,
  pantryAlerts,
  isLoading
}) => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [selectedDay, setSelectedDay] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sheetMode, setSheetMode] = useState('options'); // 'options', 'search', 'favorites', 'pantry'
  const [autoPlanLoading, setAutoPlanLoading] = useState(false);
  const [showUndoSnackbar, setShowUndoSnackbar] = useState(false);
  const [lastAutoPlan, setLastAutoPlan] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Generate week days starting from today
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 0 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  // Calculate stats
  const totalMeals = Object.values(mealPlan).reduce((acc, meals) => acc + (meals?.length || 0), 0);
  const emptyDays = weekDays.filter(day => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return !mealPlan[dateStr] || mealPlan[dateStr].length === 0;
  }).length;
  const hasAnyMeals = totalMeals > 0;
  const weekComplete = emptyDays === 0;

  // Check if week is complete and show success
  useEffect(() => {
    if (weekComplete && totalMeals >= 5 && !showSuccess) {
      setShowSuccess(true);
      haptic('success');
    }
  }, [weekComplete, totalMeals, showSuccess]);

  // Handle day tap
  const handleDayTap = (day) => {
    haptic('light');
    setSelectedDay(day);
    setSheetMode('options');
    setSheetOpen(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Search recipes
  const handleSearch = async (query) => {
    if (!query.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await api.get('/recipes/search', { 
        params: { query, number: 8 } 
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  // Add meal from search/favorites
  const handleAddMeal = (recipe) => {
    haptic('success');
    onAddMeal(selectedDay, recipe);
    setSheetOpen(false);
    toast.success(`Added ${recipe.title || recipe.recipe_title}`, {
      icon: <Check className="h-4 w-4" />
    });
  };

  // Handle auto-plan
  const handleAutoPlan = async () => {
    haptic('medium');
    setAutoPlanLoading(true);
    
    try {
      // Store current plan for undo
      setLastAutoPlan({ ...mealPlan });
      
      await onAutoPlan();
      
      setShowUndoSnackbar(true);
      setTimeout(() => setShowUndoSnackbar(false), 5000);
      
      toast.success('Meals added to your week!', {
        icon: <Sparkles className="h-4 w-4" />
      });
    } catch (error) {
      toast.error('Failed to auto-plan');
    } finally {
      setAutoPlanLoading(false);
    }
  };

  // Handle undo
  const handleUndo = () => {
    haptic('light');
    // Restore previous plan
    if (lastAutoPlan) {
      // This would need to be implemented in parent
      toast.success('Changes undone');
    }
    setShowUndoSnackbar(false);
  };

  // Format day label
  const getDayLabel = (day) => {
    if (isToday(day)) return 'Today';
    if (isTomorrow(day)) return 'Tomorrow';
    return format(day, 'EEEE');
  };

  // Get suggestion for today
  const getSuggestion = () => {
    if (pantryAlerts && pantryAlerts.length > 0) {
      return {
        type: 'pantry',
        title: `Use ${pantryAlerts[0].name}`,
        subtitle: `Expiring in ${pantryAlerts[0].daysLeft || 2} days`,
        item: pantryAlerts[0]
      };
    }
    if (favorites && favorites.length > 0) {
      return {
        type: 'favorite',
        title: favorites[0].recipe_title,
        subtitle: 'From your favorites',
        item: favorites[0]
      };
    }
    return null;
  };

  const suggestion = getSuggestion();

  return (
    <div className="min-h-screen pb-24" data-testid="mobile-meal-planner">
      {/* Header */}
      <div className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 px-4 py-3 border-b">
        <h1 className="text-xl font-semibold font-['Playfair_Display']">Meal Planner</h1>
        <p className="text-sm text-muted-foreground">
          {totalMeals} meal{totalMeals !== 1 ? 's' : ''} planned this week
        </p>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* ⚡ Smart Suggestion */}
        {suggestion && !weekComplete && (
          <Card 
            className="border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 cursor-pointer active:scale-[0.98] transition-transform"
            onClick={() => {
              haptic('medium');
              const todayDate = weekDays.find(d => isToday(d)) || weekDays[0];
              if (suggestion.type === 'pantry') {
                setSelectedDay(todayDate);
                setSheetMode('pantry');
                setSheetOpen(true);
              } else {
                handleAddMeal({
                  id: suggestion.item.recipe_id,
                  title: suggestion.item.recipe_title,
                  image: suggestion.item.recipe_image
                });
              }
            }}
            data-testid="smart-suggestion"
          >
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {suggestion.type === 'pantry' ? (
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  ) : (
                    <Heart className="h-5 w-5 text-rose-500" />
                  )}
                  <div>
                    <p className="font-medium text-sm">{suggestion.title}</p>
                    <p className="text-xs text-muted-foreground">{suggestion.subtitle}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-8">
                  Add
                  <Plus className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 🎉 Success State */}
        {showSuccess && weekComplete && (
          <Card className="border-green-200 dark:border-green-800 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
            <CardContent className="py-6 text-center">
              <PartyPopper className="h-10 w-10 mx-auto mb-3 text-green-600" />
              <h3 className="font-semibold text-lg mb-1">Your week is planned!</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Want us to make your grocery list?
              </p>
              <Button 
                className="w-full rounded-full active:scale-95 transition-transform"
                onClick={() => { haptic('medium'); onGenerateGroceryList(); }}
                data-testid="success-generate-list"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Build My Grocery List
              </Button>
            </CardContent>
          </Card>
        )}

        {/* 🗓️ Vertical Day Stack */}
        <div className="space-y-3">
          {weekDays.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const meals = mealPlan[dateStr] || [];
            const hasMeals = meals.length > 0;
            const isCurrentDay = isToday(day);
            const isPastDay = isPast(day) && !isToday(day);

            return (
              <Card 
                key={dateStr}
                className={`border transition-all ${
                  isCurrentDay 
                    ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20' 
                    : isPastDay 
                    ? 'opacity-60' 
                    : 'border-border/50'
                }`}
                data-testid={`day-card-${dateStr}`}
              >
                <CardContent className="py-3 px-4">
                  {/* Day Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{getDayLabel(day)}</span>
                      {isCurrentDay && (
                        <Badge variant="default" className="text-xs h-5">Today</Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(day, 'MMM d')}
                    </span>
                  </div>

                  {/* Meals or Add Button */}
                  {hasMeals ? (
                    <div className="space-y-2">
                      {meals.map((meal, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
                        >
                          {meal.recipe_image && (
                            <img 
                              src={meal.recipe_image} 
                              alt={meal.recipe_title}
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          )}
                          <span className="flex-1 text-sm font-medium truncate">
                            {meal.recipe_title}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground"
                            onClick={(e) => {
                              e.stopPropagation();
                              haptic('light');
                              onRemoveMeal(dateStr, idx);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {/* Add another meal */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-8 text-xs text-muted-foreground"
                        onClick={() => handleDayTap(day)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add another
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full h-12 border-dashed text-muted-foreground hover:text-foreground hover:border-primary active:scale-[0.98] transition-all"
                      onClick={() => handleDayTap(day)}
                      data-testid={`add-meal-${dateStr}`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {isCurrentDay ? "What's for dinner?" : "Add meal"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* 📌 Sticky Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t p-4 space-y-2 z-20" data-testid="bottom-actions">
        {/* Undo Snackbar */}
        {showUndoSnackbar && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted mb-2">
            <span className="text-sm">Meals added</span>
            <Button variant="ghost" size="sm" onClick={handleUndo}>
              <Undo2 className="h-4 w-4 mr-1" />
              Undo
            </Button>
          </div>
        )}

        <div className="flex gap-2">
          {/* Plan My Week - Show if empty days */}
          {emptyDays > 0 && isPro && (
            <Button
              variant="outline"
              className="flex-1 h-12 active:scale-95 transition-transform"
              onClick={handleAutoPlan}
              disabled={autoPlanLoading}
              data-testid="plan-my-week-btn"
            >
              {autoPlanLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Plan My Week
            </Button>
          )}

          {/* Generate List - Show if meals exist */}
          {hasAnyMeals && !weekComplete && (
            <Button
              className="flex-1 h-12 active:scale-95 transition-transform"
              onClick={() => { haptic('medium'); onGenerateGroceryList(); }}
              data-testid="build-grocery-list-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Build My Grocery List
            </Button>
          )}

          {/* If no meals and not pro, show simple add */}
          {!hasAnyMeals && !isPro && (
            <Button
              className="flex-1 h-12 active:scale-95 transition-transform"
              onClick={() => {
                const todayDay = weekDays.find(d => isToday(d)) || weekDays[0];
                handleDayTap(todayDay);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Meal
            </Button>
          )}
        </div>
      </div>

      {/* 🟢 Add Meal Bottom Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl">
          <SheetHeader className="text-left pb-4">
            <SheetTitle className="font-['Playfair_Display']">
              {selectedDay && getDayLabel(selectedDay)}
            </SheetTitle>
            <SheetDescription>
              {selectedDay && format(selectedDay, 'MMMM d, yyyy')}
            </SheetDescription>
          </SheetHeader>

          {/* Sheet Content based on mode */}
          {sheetMode === 'options' && (
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full h-14 justify-start text-left active:scale-[0.98] transition-transform"
                onClick={() => { haptic('light'); setSheetMode('search'); }}
              >
                <Search className="h-5 w-5 mr-3 text-primary" />
                <div>
                  <p className="font-medium">Search recipes</p>
                  <p className="text-xs text-muted-foreground">Find something new</p>
                </div>
              </Button>

              {favorites && favorites.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full h-14 justify-start text-left active:scale-[0.98] transition-transform"
                  onClick={() => { haptic('light'); setSheetMode('favorites'); }}
                >
                  <Heart className="h-5 w-5 mr-3 text-rose-500" />
                  <div>
                    <p className="font-medium">From favorites</p>
                    <p className="text-xs text-muted-foreground">{favorites.length} saved recipes</p>
                  </div>
                </Button>
              )}

              {pantryAlerts && pantryAlerts.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full h-14 justify-start text-left border-amber-200 active:scale-[0.98] transition-transform"
                  onClick={() => { haptic('light'); setSheetMode('pantry'); }}
                >
                  <AlertTriangle className="h-5 w-5 mr-3 text-amber-600" />
                  <div>
                    <p className="font-medium">Use pantry items</p>
                    <p className="text-xs text-amber-600">{pantryAlerts.length} item{pantryAlerts.length > 1 ? 's' : ''} expiring soon</p>
                  </div>
                </Button>
              )}

              {isPro && (
                <Button
                  variant="outline"
                  className="w-full h-14 justify-start text-left active:scale-[0.98] transition-transform"
                  onClick={() => {
                    haptic('medium');
                    setSheetOpen(false);
                    handleAutoPlan();
                  }}
                >
                  <Sparkles className="h-5 w-5 mr-3 text-primary" />
                  <div>
                    <p className="font-medium">Suggest for me</p>
                    <p className="text-xs text-muted-foreground">AI picks based on preferences</p>
                  </div>
                </Button>
              )}
            </div>
          )}

          {sheetMode === 'search' && (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                className="mb-2"
                onClick={() => setSheetMode('options')}
              >
                ← Back
              </Button>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                  className="pl-10 h-12"
                  autoFocus
                />
              </div>

              <Button 
                onClick={() => handleSearch(searchQuery)} 
                disabled={searchLoading || !searchQuery.trim()}
                className="w-full h-10"
              >
                {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </Button>

              {/* Search Results */}
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {searchResults.map((recipe) => (
                  <div
                    key={recipe.id}
                    className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted active:scale-[0.98] transition-all"
                    onClick={() => handleAddMeal(recipe)}
                  >
                    {recipe.image && (
                      <img 
                        src={recipe.image} 
                        alt={recipe.title}
                        className="h-12 w-12 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{recipe.title}</p>
                      {recipe.readyInMinutes && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {recipe.readyInMinutes} min
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {sheetMode === 'favorites' && (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                className="mb-2"
                onClick={() => setSheetMode('options')}
              >
                ← Back
              </Button>

              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {favorites.map((fav) => (
                  <div
                    key={fav.id}
                    className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted active:scale-[0.98] transition-all"
                    onClick={() => handleAddMeal({
                      id: fav.recipe_id,
                      title: fav.recipe_title,
                      image: fav.recipe_image
                    })}
                  >
                    {fav.recipe_image && (
                      <img 
                        src={fav.recipe_image} 
                        alt={fav.recipe_title}
                        className="h-12 w-12 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{fav.recipe_title}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Heart className="h-3 w-3 text-rose-500" />
                        Saved favorite
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {sheetMode === 'pantry' && (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                className="mb-2"
                onClick={() => setSheetMode('options')}
              >
                ← Back
              </Button>

              <p className="text-sm text-muted-foreground">
                Find recipes using ingredients that are expiring soon
              </p>

              <div className="space-y-2">
                {pantryAlerts.map((item, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    className="w-full h-14 justify-start border-amber-200 active:scale-[0.98] transition-transform"
                    onClick={() => {
                      haptic('light');
                      setSheetOpen(false);
                      navigate(`/recipes?query=${encodeURIComponent(item.name)}`);
                    }}
                  >
                    <AlertTriangle className="h-5 w-5 mr-3 text-amber-600" />
                    <div className="text-left">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs text-amber-600">Expiring soon</p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </Button>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileMealPlanner;
